import { useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import L from 'leaflet';
import { useTeamOverview, type PromoterOverview } from '../../hooks/use-team-overview';
import MobileMap from '../../components/maps/MobileMap';
import { Loader2, WifiOff } from 'lucide-react';
import { useOffline } from '../../hooks/use-offline';

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

// Color mapping per promoter status
const STATUS_COLORS: Record<PromoterOverview['status'], string> = {
  in_field: '#16a34a',   // green-600
  delayed: '#d97706',    // amber-600
  absent: '#dc2626',     // red-600
  finished: '#2563eb',   // blue-600
  in_transit: '#6b7280', // gray-500
};

const STATUS_LABELS: Record<PromoterOverview['status'], string> = {
  in_field: 'Em campo',
  delayed: 'Atrasado',
  absent: 'Ausente',
  finished: 'Finalizado',
  in_transit: 'Em trânsito',
};

function createPromoterIcon(color: string, initials: string): L.DivIcon {
  return L.divIcon({
    className: 'promoter-marker',
    html: `<div style="
      width: 36px; height: 36px; border-radius: 50%;
      background: ${color};
      color: white;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 12px; letter-spacing: 0.5px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.35);
    ">${initials}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -22],
  });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function TeamMap() {
  const [, setLocation] = useLocation();
  const { data, isLoading, cachedTimestamp } = useTeamOverview(30000);
  const { isOnline } = useOffline();

  const promotersWithLocation = (data?.promoters ?? []).filter(
    (p) => p.last_location !== null,
  );

  const handleMapReady = useCallback(
    (map: L.Map) => {
      const bounds = L.latLngBounds([]);

      promotersWithLocation.forEach((promoter) => {
        const { latitude, longitude } = promoter.last_location!;
        const color = STATUS_COLORS[promoter.status];
        const initials = getInitials(promoter.name);
        const icon = createPromoterIcon(color, initials);

        const progressText = promoter.route_progress
          ? `${promoter.route_progress.visited}/${promoter.route_progress.total} lojas`
          : '— lojas';

        const storeText = promoter.current_store
          ? promoter.current_store.name
          : 'Sem loja ativa';

        const popupContent = `
          <div style="min-width: 180px; font-family: system-ui, sans-serif;">
            <div style="font-weight: 700; font-size: 14px; color: #111827; margin-bottom: 4px;">
              ${escapeHtml(promoter.name)}
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 2px;">
              📍 ${escapeHtml(storeText)}
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
              ✅ ${escapeHtml(progressText)}
            </div>
            <a
              href="#"
              onclick="window.__teamMapNavigate && window.__teamMapNavigate('${escapeHtml(promoter.promoter_id)}'); return false;"
              style="
                display: block; text-align: center;
                background: #2563eb; color: white;
                border-radius: 8px; padding: 6px 12px;
                font-size: 12px; font-weight: 600;
                text-decoration: none;
              "
            >Ver detalhes</a>
          </div>
        `;

        const marker = L.marker([latitude, longitude], { icon }).addTo(map);
        marker.bindPopup(popupContent, { maxWidth: 220, className: 'promoter-popup' });

        // Tap marker → open popup
        marker.on('click', () => {
          marker.openPopup();
        });

        bounds.extend([latitude, longitude]);
      });

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [48, 48] });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promotersWithLocation.length],
  );

  // Expose navigate helper so popup link can trigger wouter navigation
  useEffect(() => {
    (window as any).__teamMapNavigate = (id: string) => setLocation(`/supervisor/team/${id}`);
    return () => { delete (window as any).__teamMapNavigate; };
  }, [setLocation]);

  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-900">
      {/* Offline banner */}
      {!isOnline && cachedTimestamp && (
        <div className="bg-amber-500 text-white text-xs text-center py-1.5 flex items-center justify-center gap-1 z-10 shrink-0">
          <WifiOff size={12} />
          Sem conexão — dados de{' '}
          {new Date(cachedTimestamp).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}

      {/* Full-screen map — occupies all remaining space above the nav */}
      <div className="flex-1 relative overflow-hidden">
        <MobileMap
          className="h-full w-full"
          onMapReady={handleMapReady}
        />

        {/* No-location notice */}
        {promotersWithLocation.length === 0 && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/60 text-white text-sm rounded-xl px-4 py-3 text-center mx-8">
              Nenhum promotor com localização disponível
            </div>
          </div>
        )}

        {/* Promoter count badge */}
        <div className="absolute top-3 left-3 z-[400] bg-white/90 backdrop-blur-sm rounded-xl px-3 py-1.5 shadow-lg">
          <span className="text-xs font-semibold text-gray-700">
            {promotersWithLocation.length} no mapa
          </span>
        </div>

        {/* Refresh label */}
        <div className="absolute top-3 right-3 z-[400] bg-white/90 backdrop-blur-sm rounded-xl px-3 py-1.5 shadow-lg">
          <span className="text-[10px] text-gray-500">Atualiza a cada 30s</span>
        </div>
      </div>

      {/* Legend bar */}
      <div className="bg-white border-t border-gray-100 px-3 py-2 shrink-0 z-10">
        <div className="flex items-center justify-around gap-1 overflow-x-auto scrollbar-none">
          {(Object.entries(STATUS_COLORS) as [PromoterOverview['status'], string][]).map(
            ([status, color]) => (
              <div key={status} className="flex items-center gap-1 shrink-0">
                <div
                  style={{ background: color }}
                  className="w-3 h-3 rounded-full border border-white shadow-sm"
                />
                <span className="text-[10px] text-gray-600 whitespace-nowrap">
                  {STATUS_LABELS[status]}
                </span>
              </div>
            ),
          )}
        </div>
      </div>

      {/* Popup styles */}
      <style>{`
        .promoter-popup .leaflet-popup-content-wrapper {
          border-radius: 12px;
          padding: 0;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
        }
        .promoter-popup .leaflet-popup-content {
          margin: 12px;
        }
        .promoter-popup .leaflet-popup-tip {
          background: white;
        }
      `}</style>
    </div>
  );
}
